console.log("VPX WORKOUT FILE LOADED");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Workout module aborted.");
        return;
    }

    /*
    ==========================================
    Exercise Catalogue
    ------------------------------------------
    Each exercise is tagged "compound" (multi-
    joint) or "isolation" (single-joint) — this
    tag drives sets/reps/rest/RIR/tempo below,
    since these variables genuinely differ by
    exercise type, not just by goal.
    ==========================================
    */

    const EXERCISES = {
        squat: { nameKey: "exercise_squat", type: "compound" },
        bench_press: { nameKey: "exercise_bench_press", type: "compound" },
        bent_over_row: { nameKey: "exercise_bent_over_row", type: "compound" },
        overhead_press: { nameKey: "exercise_overhead_press", type: "compound" },
        romanian_deadlift: { nameKey: "exercise_romanian_deadlift", type: "compound" },
        plank: { nameKey: "exercise_plank", type: "isolation" },
        lat_pulldown: { nameKey: "exercise_lat_pulldown", type: "compound" },
        bicep_curl: { nameKey: "exercise_bicep_curl", type: "isolation" },
        tricep_pushdown: { nameKey: "exercise_tricep_pushdown", type: "isolation" },
        leg_press: { nameKey: "exercise_leg_press", type: "compound" },
        leg_curl: { nameKey: "exercise_leg_curl", type: "isolation" },
        calf_raise: { nameKey: "exercise_calf_raise", type: "isolation" },
        incline_dumbbell_press: { nameKey: "exercise_incline_dumbbell_press", type: "compound" },
        lateral_raise: { nameKey: "exercise_lateral_raise", type: "isolation" },
        deadlift: { nameKey: "exercise_deadlift", type: "compound" },
        pull_up: { nameKey: "exercise_pull_up", type: "compound" },
        barbell_row: { nameKey: "exercise_barbell_row", type: "compound" },
        face_pull: { nameKey: "exercise_face_pull", type: "isolation" }
    };

    /*
    ==========================================
    Splits — auto-selected by weekly frequency
    ==========================================
    */

    const SPLITS = {
        full_body: {
            nameKey: "split_full_body",
            days: [
                {
                    nameKey: "workout_full_body",
                    exerciseIds: ["squat", "bench_press", "bent_over_row", "overhead_press", "romanian_deadlift", "plank"]
                }
            ]
        },
        upper_lower: {
            nameKey: "split_upper_lower",
            days: [
                {
                    nameKey: "workout_upper",
                    exerciseIds: ["bench_press", "bent_over_row", "overhead_press", "lat_pulldown", "bicep_curl", "tricep_pushdown"]
                },
                {
                    nameKey: "workout_lower",
                    exerciseIds: ["squat", "romanian_deadlift", "leg_press", "leg_curl", "calf_raise", "plank"]
                }
            ]
        },
        ppl: {
            nameKey: "split_ppl",
            days: [
                {
                    nameKey: "workout_push",
                    exerciseIds: ["bench_press", "overhead_press", "incline_dumbbell_press", "lateral_raise", "tricep_pushdown"]
                },
                {
                    nameKey: "workout_pull",
                    exerciseIds: ["deadlift", "pull_up", "barbell_row", "face_pull", "bicep_curl"]
                },
                {
                    nameKey: "workout_legs",
                    exerciseIds: ["squat", "romanian_deadlift", "leg_press", "leg_curl", "calf_raise"]
                }
            ]
        }
    };

    /*
    ==========================================
    Programming Matrix — goal × exercise type
    ------------------------------------------
    Grounded in current resistance-training
    literature rather than one-size-fits-all
    numbers:
      - Compound lifts get longer rest (better
        supports the loads needed for hypertrophy
        and strength across sets — Schoenfeld
        2016, Grgic et al. 2017).
      - Isolation work gets a prescribed tempo
        (2-3s controlled eccentric) since it's
        where tempo/mind-muscle control adds the
        most value; compounds are just cued
        "controlled" without an artificial slow
        count, since bar speed also matters there.
      - Fat loss keeps COMPOUND lifts in a
        moderate 8-12 rep range (same intent as
        muscle gain) — the point of resistance
        training in a deficit is to preserve
        strength/muscle, which depends on load,
        not rep count. Only ISOLATION work shifts
        to higher reps (12-15) + shorter rest +
        supersets, adding time-efficient metabolic
        density without compromising the main
        lifts.
      - RIR 2-3 across fat loss (vs 1-2 for muscle
        gain) since recovery capacity is typically
        reduced in a calorie deficit.
      - Muscle gain leans on RIR 1-2 to maximize
        effective volume without excessive fatigue.
    ==========================================
    */

    const PROGRAMMING = {
        muscle_gain: {
            compound: { sets: 4, reps: "6-10", restKey: "rest_long", rirKey: "rir_1_2", tempoKey: null },
            isolation: { sets: 3, reps: "10-15", restKey: "rest_moderate", rirKey: "rir_0_1", tempoKey: "tempo_controlled" }
        },
        fat_loss: {
            compound: { sets: 3, reps: "8-12", restKey: "rest_moderate", rirKey: "rir_2_3", tempoKey: null },
            isolation: { sets: 3, reps: "12-15", restKey: "rest_short", rirKey: "rir_2_3", tempoKey: "tempo_controlled" }
        },
        maintenance: {
            compound: { sets: 3, reps: "8-12", restKey: "rest_moderate", rirKey: "rir_2_3", tempoKey: null },
            isolation: { sets: 3, reps: "10-12", restKey: "rest_moderate", rirKey: "rir_2_3", tempoKey: "tempo_controlled" }
        }
    };

    function selectSplitId(trainingDaysCount) {
        if (!trainingDaysCount || trainingDaysCount <= 0) return null;
        if (trainingDaysCount <= 3) return "full_body";
        if (trainingDaysCount === 4) return "upper_lower";
        return "ppl";
    }

    function sortMondayFirst(a, b) {
        const av = a === 0 ? 7 : a;
        const bv = b === 0 ? 7 : b;
        return av - bv;
    }

    /*
    ==========================================
    Superset Pairing (fat loss only)
    ------------------------------------------
    Pairs consecutive isolation exercises within
    a day into supersets — added density and
    time-efficiency, appropriate specifically for
    a fat-loss-phase session. Compound lifts are
    always kept as straight sets (technique and
    load matter more there than time-density).
    ==========================================
    */

    function applySupersets(exercises, goal) {

        if (goal !== "fat_loss") return exercises;

        let groupCharCode = 65; // 'A'
        let i = 0;

        while (i < exercises.length - 1) {
            if (exercises[i].type === "isolation" && exercises[i + 1].type === "isolation") {
                const label = String.fromCharCode(groupCharCode);
                exercises[i].supersetGroup = label;
                exercises[i + 1].supersetGroup = label;
                groupCharCode++;
                i += 2;
            } else {
                i++;
            }
        }

        return exercises;
    }

    function buildDayEntry(splitDay, goal, jsDay) {

        const exercises = splitDay.exerciseIds.map(function (id) {
            const ex = EXERCISES[id];
            const scheme = PROGRAMMING[goal][ex.type];
            return {
                id: id,
                nameKey: ex.nameKey,
                type: ex.type,
                sets: scheme.sets,
                reps: scheme.reps,
                restKey: scheme.restKey,
                rirKey: scheme.rirKey,
                tempoKey: scheme.tempoKey,
                supersetGroup: null
            };
        });

        applySupersets(exercises, goal);

        return {
            jsDay: jsDay,
            nameKey: splitDay.nameKey,
            exercises: exercises
        };
    }

    /*
    ==========================================
    Public API
    ==========================================
    */

    const Workout = {};

    Workout.buildPlan = function (profile) {

        const trainingDaysCount = profile.trainingDaysPerWeek || 0;
        const splitId = selectSplitId(trainingDaysCount);

        if (!splitId) return null;

        const split = SPLITS[splitId];
        const goal = PROGRAMMING[profile.goal] ? profile.goal : "maintenance";

        const scheduleDays = (profile.trainingDays && profile.trainingDays.length)
            ? profile.trainingDays.slice().sort(sortMondayFirst)
            : null;

        const days = scheduleDays
            ? scheduleDays.map(function (jsDay, idx) {
                return buildDayEntry(split.days[idx % split.days.length], goal, jsDay);
            })
            : split.days.map(function (splitDay) {
                return buildDayEntry(splitDay, goal, null);
            });

        return {
            splitId: splitId,
            splitNameKey: split.nameKey,
            goal: goal,
            days: days
        };
    };

    /*
    ==========================================
    Export
    ==========================================
    */

    window.VORYNEX.workout = Workout;

    // Exposed so other modules (e.g. the Progress hub, which lists
    // logged exercises that may not be part of today's active program)
    // can look up a human-readable name from a bare exercise id.
    Workout.exercises = EXERCISES;
    console.log("Workout module exported.");

})();